import * as d3 from "d3";
import { useEffect, useState } from "react";

type UseGetTickMarksParams = {
  duration: number;
  dataPoints: unknown[];
  isLoading: boolean;
  containerWidth: number; // NEW PARAM
  minTickSpacing?: number; // OPTIONAL - default to 50
};

export const useGetTickMarks = ({
  duration,
  dataPoints,
  isLoading,
  containerWidth,
  minTickSpacing = 50,
}: UseGetTickMarksParams): number[] => {
  const [xTicks, setXTicks] = useState<number[]>([]);

  useEffect(() => {
    const generateTickStep = () => {
      const intervals = [1, 2, 5, 10, 15, 20, 30, 60, 120, 300, 600, 900, 1800, 3600];

      const maxTicks = Math.floor(containerWidth / minTickSpacing);

      return (
        intervals.find((interval) => duration / interval <= maxTicks) ?? intervals[intervals.length - 1] // fallback to max interval
      );
    };

    if (!isLoading && duration > 0 && containerWidth > 0) {
      const tickStep = generateTickStep();
      const ticks = d3.range(0, duration, tickStep);
      setXTicks(ticks);
    }
  }, [duration, JSON.stringify(dataPoints), isLoading, containerWidth, minTickSpacing]);

  return xTicks;
};

export function formatTimeDisplay(seconds: number, style: "verbose" | "digital" = "verbose"): string {
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (style === "digital") {
    if (hours > 0) {
      return [
        String(hours).padStart(2, "0"),
        String(minutes).padStart(2, "0"),
        String(remainingSeconds).padStart(2, "0"),
      ].join(":");
    }

    return [String(minutes).padStart(2, "0"), String(remainingSeconds).padStart(2, "0")].join(":");
  }

  const hourPart = hours > 0 ? `${hours}h` : "";
  const minutePart = minutes > 0 ? `${minutes}m` : "";
  const secondPart = remainingSeconds > 0 ? `${remainingSeconds}s` : "";

  return [hourPart, minutePart, secondPart].filter(Boolean).join(" ") || "0s";
}
